import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from 'react-bootstrap/NavDropdown';
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import "../../i18n";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import "./Navbar.scss";
import { motion } from "framer-motion";
import useDetectView from "../Hook/useDetectView";
import { View } from "../../Data/Constants";
import { Logo_Circle } from "../../Data/Constants";

interface NavBarProps {
  delay?: boolean;
}

export const NavBar: React.FC<NavBarProps> = ({ delay = false }) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string | null>(null);
  const currentView = useDetectView();
  const [opacity, setOpacity] = useState(0);
  const location = useLocation();
  const [sticky, setSticky] = useState(false);
  const navbarRef = useRef<HTMLDivElement | null>(null);

  // Can delay the navbar apparition for smoother cinematic!
  const isTestPage = location.pathname === '/projects/quartieroviv';
  const [showDelayed, setShowDelayed] = useState(!isTestPage);
  


  // Delay effect
  useEffect(() => {
    if (isTestPage) {
      const timer = setTimeout(() => {
        setShowDelayed(true);
      }, 3000); 
      return () => clearTimeout(timer);
    }
  }, [isTestPage]);

  const adjustNavbar = useCallback(() => {
    if (location.pathname === "/" || location.pathname==="/projects/quartieroviv") {
      const newOpacity = Math.min(window.scrollY / 200, 1);
      setOpacity(newOpacity);

      if (window.scrollY > 100) {
        setSticky(true);
      } else {
        setSticky(false);
      }
    } else {
      setOpacity(1);  // Always solid on other pages
      setSticky(true); // Always sticky
    }
  }, [location.pathname]);

  const onScroll = useCallback(() => {
    adjustNavbar();
  }, [adjustNavbar]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    adjustNavbar();
    return () => window.removeEventListener("scroll", onScroll);
  }, [adjustNavbar, onScroll]);

  const handleClickfunc = async () => {
    await /* API call here */
      navigate('/nousrejoindre');
  }

  const currentLogo = Logo_Circle;

  useEffect(() => {
    setCurrentLanguage(i18n.language.toUpperCase());
  }, [i18n.language]);

  interface ClassNameFuncProps {
    isActive: boolean;
  }

  const classNameFunc = ({ isActive }: ClassNameFuncProps) =>
    isActive ? "navbar-item active-nav-item " : "navbar-item ";

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    if (lng === "en") {
      setCurrentLanguage("EN");
    }
    if (lng === "fr") {
      setCurrentLanguage("FR");
    }

    // Set a cookie for language, for 365 days
    Cookies.set('language', lng, { expires: 365 });
  };

  
  if (!showDelayed) return null;

  return (
    <motion.div
      ref={navbarRef}
      initial={{ opacity: 0, x: '-100vw' }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 1 }}
      className={`custom-nav ${sticky && currentView !== View.Mobile ? 'sticky-nav' : ''}`}
      style={{
        position: sticky ? "sticky" : "absolute",
        top: sticky && currentView !== View.Mobile ? 0 : 'initial', // Add this line
        backgroundColor: currentView === View.Desktop || currentView === View.LargeTablet || currentView === View.Tablet ? `rgba(16, 26, 44, ${opacity})` : "white"
      }}
    >
      <Navbar expand="md">
        <Container>
          <Navbar.Brand>
            <Link to="/">
              <img className="img-logo" src={currentLogo} alt="Simdev Logo"></img>
            </Link>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <NavLink className={classNameFunc} to="/histoire">
                {t("navbar.history-link")}
              </NavLink>
              <NavLink to="/construction" className={classNameFunc}>
                {t("navbar.construction-link")}
              </NavLink>
              <NavLink className={classNameFunc} to="/gestion">
                {t("navbar.management-link")}
              </NavLink>
              <NavLink className={classNameFunc} to="/projets">
                {t("navbar.projects-link")}
              </NavLink>
              <NavLink className={classNameFunc} to="/carrieres">
                {t("navbar.careers-link")}
              </NavLink>
              {
                currentView === View.Tablet || currentView === View.Mobile ?
                  <NavLink className={classNameFunc} to="/nousrejoindre">{t("navbar.contact-button")}</NavLink>
                  :
                  <></>
              }
            </Nav>

            {currentView === View.Desktop || currentView === View.LargeTablet ?
              <span>
                <button
                  className={sticky ? "contact-button" : "contact-button-transparent"}
                  onClick={handleClickfunc}
                >
                  {t("navbar.contact-button")}
                </button>
              </span> : <></>
            }
            <NavDropdown
              className="language-dropdown"
              title={currentLanguage}
              menuVariant="light">
              <NavDropdown.Item onClick={() => changeLanguage("en")}>English</NavDropdown.Item>
              <NavDropdown.Item onClick={() => changeLanguage("fr")}>Français</NavDropdown.Item>
            </NavDropdown>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </motion.div>
  );
};
